import type { ReactNode } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { StringNameAvatar } from '@/components/lemmala-design-system/avatar/string-name-avatar'

export type AuthorLineProps = {
  name: string
  avatarUrl?: string
  // Replaces the avatar for rows attributed to something other than a person.
  avatar?: ReactNode
  date?: string
  dateTitle?: string
  hideName?: boolean
  size?: 'default' | 'small'
  // Name text-style override for rows on a tighter ramp, e.g. text-label-small.
  textClassName?: string
  // Merged onto the row root, e.g. min-w-0 inside a truncating flex container.
  className?: string
  // Trailing slot for row-specific extras, e.g. an edited label or an actions menu.
  children?: ReactNode
}

export const AuthorLine = ({
  name,
  avatarUrl,
  avatar,
  date,
  dateTitle,
  hideName,
  size = 'default',
  textClassName,
  className,
  children,
}: AuthorLineProps) => (
  <div className={cn('flex items-center gap-1.5', className)}>
    {avatar ?? (
      <StringNameAvatar
        name={name}
        imageSrc={avatarUrl}
        type={avatarUrl ? 'image' : 'initials'}
        size={size === 'small' ? 'extra-small' : 'small'}
        className='shrink-0'
      />
    )}
    {!hideName && (
      <span
        title={name}
        className={cn(
          'min-w-0 truncate text-main-foreground-primary',
          size === 'small' ? 'text-body-small' : 'text-label-default',
          textClassName,
        )}
      >
        {name}
      </span>
    )}
    {date && (
      <span title={dateTitle} className='shrink-0 text-main-foreground-tertiary text-body-small'>
        {date}
      </span>
    )}
    {children}
  </div>
)
